import { Suspense } from 'react';
import { useT } from '@/i18n/LocaleProvider';
import { ChunkFailBoundary } from '@/routing/ChunkFailBoundary';
import { lazyRoute } from '@/routing/lazyRoute';

const CommandPalette = lazyRoute(
  () => import('./CommandPalette'),
  (module) => module.CommandPalette,
);

/** Fetches the palette's chunk now, so the first ⌘K opens at once (the shell does it when idle). */
export const preloadCommandPalette = (): Promise<void> => CommandPalette.preload();

/**
 * The command palette as the shell draws it (`Layout`): its own chunk (T-0559), contained. Offline
 * its import fails, and thrown out of the palette that took the page to the route error screen; here
 * the failure is drawn where the palette opens, Try again fetches the chunk afresh, and Close (or
 * Escape) puts it away like the palette.
 */
export function CommandPaletteSlot({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const t = useT();
  return (
    <ChunkFailBoundary
      retry={CommandPalette.preload}
      failed={(retry) =>
        open ? (
          <div
            role="alert"
            className="fixed inset-x-4 top-[15%] z-50 mx-auto max-w-lg rounded-lg border border-gray-200 bg-white p-4 text-[13px] shadow-lg dark:border-white/10 dark:bg-[#242424]"
          >
            {t('navigation.palette.failed')}{' '}
            {/* Where the palette's own field would take the caret, Try again takes it. */}
            <button type="button" onClick={retry} autoFocus className="font-medium evx-link">
              {t('common.waiting.tryAgain')}
            </button>{' '}
            <button type="button" onClick={() => onOpenChange(false)} className="evx-link">
              {t('common.panelClose.plain')}
            </button>
          </div>
        ) : null
      }
    >
      <Suspense fallback={null}>
        <CommandPalette open={open} onOpenChange={onOpenChange} />
      </Suspense>
    </ChunkFailBoundary>
  );
}
